
import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend
} from 'recharts';
import {
  Download, FileSpreadsheet, Calendar, TrendingUp, AlertCircle, Package, Users, DollarSign, Filter, Loader2, X, Search, ChevronRight
} from 'lucide-react';
import { Button, Card, Badge, useToast } from './ui';
import { InvoiceStatus, ProductType } from '../types';
import {
  reportingService,
  KPIDashboard,
  salesService,
  stockService,
} from '../src/services';
import apiClient from '../src/services/apiClient';

// Helper for formatting
const formatCurrency = (val: number | string) => new Intl.NumberFormat('fr-BJ', { style: 'currency', currency: 'XOF', maximumFractionDigits: 0 }).format(Number(val) || 0);
const formatPercent = (val: number | string | null | undefined) => {
  if (val == null) return '—';
  const num = Number(val);
  return isNaN(num) ? '—' : `${num.toFixed(1)}%`;
};
const formatDate = (d: string) => d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

// Drill-down discriminated union
type DrillDown =
  | { kind: 'ca_invoices' }
  | { kind: 'stock_products' }
  | { kind: 'unpaid_invoices' }
  | { kind: 'marge_invoices' }
  | { kind: 'day_detail'; date_jour: string }
  | { kind: 'provenance_detail'; provenance_nom: string }
  | { kind: 'stock_category'; category_nom: string }
  | { kind: 'invoice_detail'; invoice_id: string };

const DRILL_TITLES: Record<DrillDown['kind'], string> = {
  ca_invoices: "Factures - Chiffre d'Affaires",
  stock_products: 'Produits en Stock',
  unpaid_invoices: 'Factures Impayées',
  marge_invoices: 'Marge Brute Journalière',
  day_detail: 'Factures du Jour',
  provenance_detail: 'Détail Provenance',
  stock_category: 'Produits de la Catégorie',
  invoice_detail: 'Détail Facture',
};

export const ReportingModule = ({ navigate, userRole }: { navigate?: (view: any) => void; userRole?: string }) => {
  const isAdmin = userRole === 'ADMIN';
  const isCommercial = userRole === 'COMMERCIAL';
  const isMagasinier = userRole === 'MAGASINIER';
  // COMMERCIAL: cannot see Stock Valorisé, CA vs Charges, Stock Valuation
  const canSeeFinancialDetails = !isCommercial && !isMagasinier;
  // Marge: ADMIN only
  const canSeeMarge = isAdmin;
  const { toast } = useToast();
  const [period, setPeriod] = useState('year');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // API state
  const [overview, setOverview] = useState<KPIDashboard | null>(null);
  const [revenueData, setRevenueData] = useState<{ name: string; ca: number; charges: number; date_jour?: string }[]>([]);
  const [stockChartData, setStockChartData] = useState<{ name: string; value: number }[]>([]);
  const [provenanceData, setProvenanceData] = useState<{ name: string; value: number }[]>([]);
  const [overdueInvoices, setOverdueInvoices] = useState<any[]>([]);

  // Raw data preserved for drill-down
  const [rawStockData, setRawStockData] = useState<any[]>([]);
  const [rawProvenanceData, setRawProvenanceData] = useState<any[]>([]);

  // Drill-down state
  const [drillDown, setDrillDown] = useState<DrillDown | null>(null);
  const [drillDownData, setDrillDownData] = useState<any[]>([]);
  const [drillDownLoading, setDrillDownLoading] = useState(false);

  // Fetch dashboard data
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const now = new Date();
      const [dashboardRes, overdueRes, stockRes, provenanceRes] = await Promise.all([
        reportingService.getOverview(period as any),
        salesService.getUnpaidInvoices().catch(() => []),
        apiClient.get('/dashboard/stock').catch(() => ({ data: { data: { stocks: [] } } })),
        apiClient.get('/reporting/ca-par-provenance', {
          params: { annee: now.getFullYear(), mois: now.getMonth() + 1 },
        }).catch(() => ({ data: { data: { provenances: [] } } })),
      ]);

      setOverview(dashboardRes);
      setOverdueInvoices(overdueRes);

      // Build revenue chart from kpis_journaliers — preserve date_jour for drill-down
      if (dashboardRes.kpis_journaliers && dashboardRes.kpis_journaliers.length > 0) {
        const revenueItems = dashboardRes.kpis_journaliers.map((kpi: any) => ({
          name: new Date(kpi.date_jour).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }),
          ca: Number(kpi.ca_jour_ttc || 0),
          charges: Number(kpi.cout_revient_jour || 0),
          date_jour: kpi.date_jour,
        }));
        setRevenueData(revenueItems);
      }

      // Build stock chart from /dashboard/stock + preserve raw
      const stockData = stockRes?.data?.data;
      if (stockData?.stocks && stockData.stocks.length > 0) {
        setRawStockData(stockData.stocks);
        // Group by category
        const categoryMap: Record<string, number> = {};
        stockData.stocks.forEach((s: any) => {
          const catName = s.category_nom || 'Autres';
          categoryMap[catName] = (categoryMap[catName] || 0) + Number(s.total_valeur_fifo || 0);
        });
        setStockChartData(
          Object.entries(categoryMap).map(([name, value]) => ({ name, value }))
        );
      }

      // Build provenance chart from /reporting/ca-par-provenance + preserve raw
      const provData = provenanceRes?.data?.data;
      if (provData?.provenances && provData.provenances.length > 0) {
        setRawProvenanceData(provData.provenances);
        setProvenanceData(
          provData.provenances.map((p: any) => ({
            name: p.provenance_nom || 'Inconnu',
            value: Number(p.ca_ttc || 0),
          }))
        );
      }
    } catch (err: any) {
      setError(err.message || 'Erreur lors du chargement des données');
    } finally {
      setIsLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Computed values from overview or fallback
  const stockValuation = overview ? Number(overview.valeur_stock_totale) : 0;
  const totalUnpaid = overdueInvoices.reduce((acc, i) => acc + Number(i.montant_restant ?? i.solde_restant ?? i.remaining_amount ?? 0), 0);

  const COLORS = ['#16a34a', '#2563eb', '#ca8a04', '#dc2626', '#9333ea'];

  const handleExport = async (format: string) => {
    try {
      const blob = await reportingService.exportReport('kpis', format as any, { period });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `rapport_dashboard_${new Date().toISOString().split('T')[0]}.${format === 'excel' || format === 'xlsx' ? 'xlsx' : format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast(`Rapport exporté au format ${format.toUpperCase()}`, 'success');
    } catch {
      toast(`Erreur lors de l'export`, 'error');
    }
  };

  // =========================================================================
  // Drill-down handlers
  // =========================================================================

  const openDrillDown = useCallback(async (dd: DrillDown) => {
    setDrillDown(dd);
    setDrillDownData([]);
    setDrillDownLoading(true);

    try {
      switch (dd.kind) {
        case 'ca_invoices': {
          const res = await salesService.listInvoices({
            date_from: overview?.date_debut,
            date_to: overview?.date_fin,
            page_size: 100,
          });
          // Exclude DRAFT and CANCELLED to match KPI calculation
          setDrillDownData((res.data || []).filter((inv: any) => inv.status !== 'DRAFT' && inv.status !== 'CANCELLED'));
          break;
        }
        case 'stock_products': {
          const res = await apiClient.get('/stock/etat');
          setDrillDownData(res.data?.data?.items || []);
          break;
        }
        case 'unpaid_invoices': {
          setDrillDownData(overdueInvoices);
          break;
        }
        case 'marge_invoices': {
          setDrillDownData(overview?.kpis_journaliers || []);
          break;
        }
        case 'day_detail': {
          const res = await salesService.listInvoices({
            date_from: dd.date_jour,
            date_to: dd.date_jour,
            page_size: 100,
          });
          setDrillDownData((res.data || []).filter((inv: any) => inv.status !== 'DRAFT' && inv.status !== 'CANCELLED'));
          break;
        }
        case 'provenance_detail': {
          const prov = rawProvenanceData.find((p: any) => (p.provenance_nom || 'Inconnu') === dd.provenance_nom);
          setDrillDownData(prov ? [prov] : []);
          break;
        }
        case 'stock_category': {
          const filtered = rawStockData.filter((s: any) => (s.category_nom || 'Autres') === dd.category_nom);
          setDrillDownData(filtered);
          break;
        }
        case 'invoice_detail': {
          const invoice = await salesService.getInvoice(dd.invoice_id);
          setDrillDownData(invoice ? [invoice] : []);
          break;
        }
      }
    } catch {
      toast('Erreur lors du chargement des détails', 'error');
    } finally {
      setDrillDownLoading(false);
    }
  }, [overview, overdueInvoices, rawProvenanceData, rawStockData, toast]);

  const closeDrillDown = useCallback(() => {
    setDrillDown(null);
    setDrillDownData([]);
  }, []);

  // Close on Escape
  useEffect(() => {
    if (!drillDown) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') closeDrillDown(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [drillDown, closeDrillDown]);

  // =========================================================================
  // Detail renderers
  // =========================================================================

  const renderInvoiceTable = (data: any[], showRemaining = false) => {
    const total = data.reduce((acc, inv) => acc + Number(inv.montant_ttc || 0), 0);
    const totalRemaining = showRemaining ? data.reduce((acc, inv) => acc + Number(inv.montant_restant ?? inv.solde_restant ?? inv.remaining_amount ?? 0), 0) : 0;
    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-slate-500 uppercase text-xs">Code</th>
              <th className="px-4 py-3 text-left font-medium text-slate-500 uppercase text-xs">Client</th>
              <th className="px-4 py-3 text-left font-medium text-slate-500 uppercase text-xs">Date</th>
              <th className="px-4 py-3 text-right font-medium text-slate-500 uppercase text-xs">Montant TTC</th>
              {showRemaining && <th className="px-4 py-3 text-right font-medium text-slate-500 uppercase text-xs">Reste Dû</th>}
              <th className="px-4 py-3 text-center font-medium text-slate-500 uppercase text-xs">Statut</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {data.map((inv: any, i: number) => {
              const remaining = inv.montant_restant ?? inv.solde_restant ?? inv.remaining_amount ?? 0;
              return (
                <tr
                  key={inv.id || i}
                  className="hover:bg-slate-50 cursor-pointer"
                  onClick={() => inv.id && openDrillDown({ kind: 'invoice_detail', invoice_id: inv.id })}
                >
                  <td className="px-4 py-3 font-medium text-blue-600">{inv.code || '—'}</td>
                  <td className="px-4 py-3 text-slate-700">{inv.client_nom || inv.clientName || '—'}</td>
                  <td className="px-4 py-3 text-slate-500">{formatDate(inv.date_facture)}</td>
                  <td className="px-4 py-3 text-right font-semibold">{formatCurrency(Number(inv.montant_ttc || 0))}</td>
                  {showRemaining && <td className="px-4 py-3 text-right font-bold text-red-600">{formatCurrency(Number(remaining))}</td>}
                  <td className="px-4 py-3 text-center">
                    <Badge color={inv.status === 'PAID' ? 'green' : inv.status === 'OVERDUE' ? 'red' : inv.status === 'CANCELLED' ? 'gray' : 'yellow'}>
                      {inv.status || '—'}
                    </Badge>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot className="bg-slate-50 font-bold">
            <tr>
              <td colSpan={3} className="px-4 py-3 text-right text-slate-700">Total</td>
              <td className="px-4 py-3 text-right">{formatCurrency(total)}</td>
              {showRemaining && <td className="px-4 py-3 text-right text-red-600">{formatCurrency(totalRemaining)}</td>}
              <td></td>
            </tr>
          </tfoot>
        </table>
        {data.length === 0 && <p className="text-center py-8 text-slate-400">Aucune facture trouvée.</p>}
      </div>
    );
  };

  const renderStockTable = (data: any[]) => {
    const totalValue = data.reduce((acc, item) => acc + Number(item.valeur_stock || 0), 0);
    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-slate-500 uppercase text-xs">Référence</th>
              <th className="px-4 py-3 text-left font-medium text-slate-500 uppercase text-xs">Désignation</th>
              <th className="px-4 py-3 text-left font-medium text-slate-500 uppercase text-xs">Catégorie</th>
              <th className="px-4 py-3 text-right font-medium text-slate-500 uppercase text-xs">Qté Dispo</th>
              <th className="px-4 py-3 text-right font-medium text-slate-500 uppercase text-xs">Stock Total</th>
              <th className="px-4 py-3 text-right font-medium text-slate-500 uppercase text-xs">Valeur Stock</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {data.map((item: any, i: number) => (
              <tr key={item.product_id || i} className="hover:bg-slate-50">
                <td className="px-4 py-3 font-medium text-slate-800">{item.product_reference || '—'}</td>
                <td className="px-4 py-3 text-slate-700">{item.product_designation || '—'}</td>
                <td className="px-4 py-3 text-slate-500">{item.categorie_nom || '—'}</td>
                <td className="px-4 py-3 text-right">{item.quantite_disponible ?? '—'}</td>
                <td className="px-4 py-3 text-right font-semibold">{item.stock_total ?? '—'}</td>
                <td className="px-4 py-3 text-right font-semibold">{formatCurrency(Number(item.valeur_stock || 0))}</td>
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-slate-50 font-bold">
            <tr>
              <td colSpan={5} className="px-4 py-3 text-right text-slate-700">Total</td>
              <td className="px-4 py-3 text-right">{formatCurrency(totalValue)}</td>
            </tr>
          </tfoot>
        </table>
        {data.length === 0 && <p className="text-center py-8 text-slate-400">Aucun produit en stock.</p>}
      </div>
    );
  };

  const renderMargeTable = (data: any[]) => {
    const totalCA = data.reduce((acc, k) => acc + Number(k.ca_jour_ttc || 0), 0);
    const totalCout = data.reduce((acc, k) => acc + Number(k.cout_revient_jour || 0), 0);
    const totalMarge = data.reduce((acc, k) => acc + Number(k.marge_brute_jour || 0), 0);
    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-slate-500 uppercase text-xs">Date</th>
              <th className="px-4 py-3 text-right font-medium text-slate-500 uppercase text-xs">CA TTC</th>
              <th className="px-4 py-3 text-right font-medium text-slate-500 uppercase text-xs">Coût Revient</th>
              <th className="px-4 py-3 text-right font-medium text-slate-500 uppercase text-xs">Marge Brute</th>
              <th className="px-4 py-3 text-right font-medium text-slate-500 uppercase text-xs">Taux Marge</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {data.map((kpi: any, i: number) => (
              <tr
                key={i}
                className="hover:bg-slate-50 cursor-pointer"
                onClick={() => kpi.date_jour && openDrillDown({ kind: 'day_detail', date_jour: kpi.date_jour })}
              >
                <td className="px-4 py-3 text-slate-700">{formatDate(kpi.date_jour)}</td>
                <td className="px-4 py-3 text-right">{formatCurrency(Number(kpi.ca_jour_ttc || 0))}</td>
                <td className="px-4 py-3 text-right text-slate-500">{formatCurrency(Number(kpi.cout_revient_jour || 0))}</td>
                <td className="px-4 py-3 text-right font-semibold text-emerald-700">{formatCurrency(Number(kpi.marge_brute_jour || 0))}</td>
                <td className="px-4 py-3 text-right">{formatPercent(kpi.taux_marge_jour)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-slate-50 font-bold">
            <tr>
              <td className="px-4 py-3 text-right text-slate-700">Total</td>
              <td className="px-4 py-3 text-right">{formatCurrency(totalCA)}</td>
              <td className="px-4 py-3 text-right">{formatCurrency(totalCout)}</td>
              <td className="px-4 py-3 text-right text-emerald-700">{formatCurrency(totalMarge)}</td>
              <td className="px-4 py-3 text-right">{totalCA > 0 ? formatPercent((totalMarge / totalCA) * 100) : '—'}</td>
            </tr>
          </tfoot>
        </table>
        {data.length === 0 && <p className="text-center py-8 text-slate-400">Aucune donnée de marge.</p>}
      </div>
    );
  };

  const renderProvenanceDetail = (data: any[]) => {
    if (data.length === 0) return <p className="text-center py-8 text-slate-400">Aucune donnée disponible.</p>;
    const prov = data[0];
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-emerald-50 rounded-lg p-4">
            <p className="text-xs text-emerald-700 font-bold uppercase">CA HT</p>
            <p className="text-lg font-bold text-emerald-900">{formatCurrency(Number(prov.ca_ht || 0))}</p>
          </div>
          <div className="bg-blue-50 rounded-lg p-4">
            <p className="text-xs text-blue-700 font-bold uppercase">CA TTC</p>
            <p className="text-lg font-bold text-blue-900">{formatCurrency(Number(prov.ca_ttc || 0))}</p>
          </div>
          {canSeeMarge && (
          <div className="bg-purple-50 rounded-lg p-4">
            <p className="text-xs text-purple-700 font-bold uppercase">Marge</p>
            <p className="text-lg font-bold text-purple-900">{formatCurrency(Number(prov.marge_brute || 0))}</p>
          </div>
          )}
          {canSeeMarge && (
          <div className="bg-amber-50 rounded-lg p-4">
            <p className="text-xs text-amber-700 font-bold uppercase">Taux Marge</p>
            <p className="text-lg font-bold text-amber-900">{formatPercent(prov.taux_marge)}</p>
          </div>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-slate-50 rounded-lg p-4">
            <p className="text-xs text-slate-500 uppercase">Produits vendus</p>
            <p className="text-xl font-bold text-slate-900">{prov.nombre_produits_vendus ?? '—'}</p>
          </div>
          <div className="bg-slate-50 rounded-lg p-4">
            <p className="text-xs text-slate-500 uppercase">Quantité totale</p>
            <p className="text-xl font-bold text-slate-900">{prov.quantite_totale ?? '—'}</p>
          </div>
          <div className="bg-slate-50 rounded-lg p-4">
            <p className="text-xs text-slate-500 uppercase">Pays d'origine</p>
            <p className="text-xl font-bold text-slate-900">{prov.provenance_pays || '—'}</p>
          </div>
        </div>
      </div>
    );
  };

  const renderCategoryProducts = (data: any[]) => {
    const totalValue = data.reduce((acc, s) => acc + Number(s.total_valeur_fifo || 0), 0);
    const totalQty = data.reduce((acc, s) => acc + Number(s.total_quantite || 0), 0);
    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-slate-500 uppercase text-xs">Site</th>
              <th className="px-4 py-3 text-right font-medium text-slate-500 uppercase text-xs">Nb Produits</th>
              <th className="px-4 py-3 text-right font-medium text-slate-500 uppercase text-xs">Quantité Totale</th>
              <th className="px-4 py-3 text-right font-medium text-slate-500 uppercase text-xs">Stock Bas</th>
              <th className="px-4 py-3 text-right font-medium text-slate-500 uppercase text-xs">Valeur FIFO</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {data.map((s: any, i: number) => (
              <tr key={i} className="hover:bg-slate-50">
                <td className="px-4 py-3 font-medium text-slate-800">{s.site_nom || '—'}</td>
                <td className="px-4 py-3 text-right">{s.nombre_produits ?? '—'}</td>
                <td className="px-4 py-3 text-right">{s.total_quantite ?? '—'}</td>
                <td className="px-4 py-3 text-right">{s.produits_stock_bas > 0 ? <span className="text-amber-600 font-semibold">{s.produits_stock_bas}</span> : '0'}</td>
                <td className="px-4 py-3 text-right font-semibold">{formatCurrency(Number(s.total_valeur_fifo || 0))}</td>
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-slate-50 font-bold">
            <tr>
              <td className="px-4 py-3 text-right text-slate-700">Total</td>
              <td className="px-4 py-3 text-right">{data.reduce((a, s) => a + (s.nombre_produits || 0), 0)}</td>
              <td className="px-4 py-3 text-right">{totalQty}</td>
              <td className="px-4 py-3 text-right">{data.reduce((a, s) => a + (s.produits_stock_bas || 0), 0)}</td>
              <td className="px-4 py-3 text-right">{formatCurrency(totalValue)}</td>
            </tr>
          </tfoot>
        </table>
        {data.length === 0 && <p className="text-center py-8 text-slate-400">Aucun produit dans cette catégorie.</p>}
      </div>
    );
  };

  const renderSingleInvoice = (data: any[]) => {
    if (data.length === 0) return <p className="text-center py-8 text-slate-400">Facture introuvable.</p>;
    const inv = data[0];
    const lignes = inv.lignes || [];
    return (
      <div className="space-y-6">
        {/* Invoice header */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="text-xs text-slate-500 uppercase">Code</p>
            <p className="font-bold text-slate-900">{inv.code}</p>
          </div>
          <div>
            <p className="text-xs text-slate-500 uppercase">Client</p>
            <p className="font-bold text-slate-900">{inv.client_nom || '—'}</p>
          </div>
          <div>
            <p className="text-xs text-slate-500 uppercase">Date</p>
            <p className="font-bold text-slate-900">{formatDate(inv.date_facture)}</p>
          </div>
          <div>
            <p className="text-xs text-slate-500 uppercase">Statut</p>
            <Badge color={inv.status === 'PAID' ? 'green' : inv.status === 'OVERDUE' ? 'red' : inv.status === 'CANCELLED' ? 'gray' : 'yellow'}>
              {inv.status}
            </Badge>
          </div>
        </div>

        {/* Amounts */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-slate-50 rounded-lg p-4">
            <p className="text-xs text-slate-500 uppercase">Montant HT</p>
            <p className="text-lg font-bold">{formatCurrency(Number(inv.montant_ht || 0))}</p>
          </div>
          <div className="bg-slate-50 rounded-lg p-4">
            <p className="text-xs text-slate-500 uppercase">TVA</p>
            <p className="text-lg font-bold">{formatCurrency(Number(inv.montant_tva || 0))}</p>
          </div>
          <div className="bg-emerald-50 rounded-lg p-4">
            <p className="text-xs text-emerald-700 uppercase">Montant TTC</p>
            <p className="text-lg font-bold text-emerald-900">{formatCurrency(Number(inv.montant_ttc || 0))}</p>
          </div>
          <div className="bg-red-50 rounded-lg p-4">
            <p className="text-xs text-red-700 uppercase">Reste Dû</p>
            <p className="text-lg font-bold text-red-900">{formatCurrency(Number(inv.montant_restant ?? 0))}</p>
          </div>
        </div>

        {/* Lines table */}
        {lignes.length > 0 && (
          <div className="overflow-x-auto">
            <h4 className="text-sm font-semibold text-slate-800 uppercase mb-2">Lignes de facture</h4>
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-2 text-left font-medium text-slate-500 uppercase text-xs">Produit</th>
                  <th className="px-4 py-2 text-right font-medium text-slate-500 uppercase text-xs">Qté</th>
                  <th className="px-4 py-2 text-right font-medium text-slate-500 uppercase text-xs">P.U.</th>
                  <th className="px-4 py-2 text-right font-medium text-slate-500 uppercase text-xs">Remise</th>
                  <th className="px-4 py-2 text-right font-medium text-slate-500 uppercase text-xs">Montant HT</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {lignes.map((l: any, i: number) => (
                  <tr key={i}>
                    <td className="px-4 py-2 text-slate-700">{l.designation || l.product_nom || '—'}</td>
                    <td className="px-4 py-2 text-right">{l.quantite}</td>
                    <td className="px-4 py-2 text-right">{formatCurrency(Number(l.prix_unitaire || 0))}</td>
                    <td className="px-4 py-2 text-right">{Number(l.taux_remise) > 0 ? `${Number(l.taux_remise)}%` : '—'}</td>
                    <td className="px-4 py-2 text-right font-semibold">{formatCurrency(Number(l.montant_ht || 0))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Link to Sales module */}
        <div className="pt-2">
          <Button size="sm" variant="secondary" onClick={() => { closeDrillDown(); if (navigate) navigate('sales'); }}>
            Voir dans le module Ventes
          </Button>
        </div>
      </div>
    );
  };

  const renderDrillDownContent = () => {
    if (drillDownLoading) {
      return (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          <span className="ml-3 text-slate-500">Chargement...</span>
        </div>
      );
    }
    if (!drillDown) return null;

    switch (drillDown.kind) {
      case 'ca_invoices':
      case 'day_detail':
        return renderInvoiceTable(drillDownData);
      case 'unpaid_invoices':
        return renderInvoiceTable(drillDownData, true);
      case 'stock_products':
        return renderStockTable(drillDownData);
      case 'marge_invoices':
        return renderMargeTable(drillDownData);
      case 'provenance_detail':
        return renderProvenanceDetail(drillDownData);
      case 'stock_category':
        return renderCategoryProducts(drillDownData);
      case 'invoice_detail':
        return renderSingleInvoice(drillDownData);
      default:
        return null;
    }
  };

  const drillDownTitle = drillDown
    ? drillDown.kind === 'day_detail'
      ? `Factures du ${formatDate(drillDown.date_jour)}`
      : drillDown.kind === 'provenance_detail'
        ? `Provenance : ${drillDown.provenance_nom}`
        : drillDown.kind === 'stock_category'
          ? `Catégorie : ${drillDown.category_nom}`
          : DRILL_TITLES[drillDown.kind]
    : '';

  // =========================================================================
  // KPI card click style
  // =========================================================================
  const kpiClickClass = 'cursor-pointer hover:shadow-md transition-shadow';

  return (
    <div className="space-y-6">
       {/* Header */}
       <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
           <h2 className="text-2xl font-bold text-slate-800">Rapports & Statistiques</h2>
           <p className="text-sm text-slate-500">Analyse de performance, stocks et trésorerie.</p>
        </div>
        <div className="flex gap-2">
           <div className="flex items-center border border-slate-300 rounded-md bg-white mr-2">
              <Calendar className="w-4 h-4 ml-3 text-slate-400" />
              <select
                className="border-none text-sm py-2 pl-2 pr-8 focus:ring-0 text-slate-700 bg-transparent"
                value={period} onChange={e => setPeriod(e.target.value)}
              >
                <option value="month">Ce Mois</option>
                <option value="quarter">Ce Trimestre</option>
                <option value="year">Cette Année</option>
              </select>
           </div>
           <Button variant="secondary" icon={Download} onClick={() => handleExport('pdf')}>PDF</Button>
           <Button variant="secondary" icon={FileSpreadsheet} onClick={() => handleExport('xlsx')}>Excel</Button>
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm flex items-center gap-2">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span>{error}</span>
          <button onClick={fetchData} className="ml-auto text-red-600 underline text-xs font-medium">Réessayer</button>
        </div>
      )}

      {/* KPI Row */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i} className="animate-pulse">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-slate-200 rounded w-9 h-9"></div>
                <div className="flex-1">
                  <div className="h-3 bg-slate-200 rounded w-24 mb-2"></div>
                  <div className="h-6 bg-slate-200 rounded w-32"></div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : (
      <div className={`grid grid-cols-1 ${canSeeFinancialDetails ? 'md:grid-cols-4' : 'md:grid-cols-2'} gap-4`}>
         <Card
           className={`bg-emerald-50 border-emerald-100 ${kpiClickClass}`}
           onClick={() => openDrillDown({ kind: 'ca_invoices' })}
         >
            <div className="flex items-center gap-3">
               <div className="p-2 bg-emerald-100 rounded text-emerald-600"><TrendingUp className="w-5 h-5"/></div>
               <div>
                 <p className="text-xs text-emerald-700 font-bold uppercase">Chiffre d'Affaires</p>
                 <p className="text-xl font-bold text-emerald-900">{formatCurrency(overview ? Number(overview.ca_total_ttc) : 0)}</p>
                 <p className="text-[10px] text-emerald-600 mt-0.5 flex items-center gap-1"><Search className="w-3 h-3" /> Voir détails</p>
               </div>
            </div>
         </Card>
         {canSeeFinancialDetails && (
         <Card
           className={`bg-blue-50 border-blue-100 ${kpiClickClass}`}
           onClick={() => openDrillDown({ kind: 'stock_products' })}
         >
            <div className="flex items-center gap-3">
               <div className="p-2 bg-blue-100 rounded text-blue-600"><Package className="w-5 h-5"/></div>
               <div>
                 <p className="text-xs text-blue-700 font-bold uppercase">Stock Valorisé (Achat)</p>
                 <p className="text-xl font-bold text-blue-900">{formatCurrency(stockValuation)}</p>
                 <p className="text-[10px] text-blue-600 mt-0.5 flex items-center gap-1"><Search className="w-3 h-3" /> Voir détails</p>
               </div>
            </div>
         </Card>
         )}
         <Card
           className={`bg-red-50 border-red-100 ${kpiClickClass}`}
           onClick={() => openDrillDown({ kind: 'unpaid_invoices' })}
         >
            <div className="flex items-center gap-3">
               <div className="p-2 bg-red-100 rounded text-red-600"><AlertCircle className="w-5 h-5"/></div>
               <div>
                 <p className="text-xs text-red-700 font-bold uppercase">Impayés Clients</p>
                 <p className="text-xl font-bold text-red-900">{formatCurrency(totalUnpaid)}</p>
                 <p className="text-[10px] text-red-600 mt-0.5 flex items-center gap-1"><Search className="w-3 h-3" /> Voir détails</p>
               </div>
            </div>
         </Card>
         {canSeeMarge && (
         <Card
           className={`bg-purple-50 border-purple-100 ${kpiClickClass}`}
           onClick={() => openDrillDown({ kind: 'marge_invoices' })}
         >
            <div className="flex items-center gap-3">
               <div className="p-2 bg-purple-100 rounded text-purple-600"><Users className="w-5 h-5"/></div>
               <div>
                 <p className="text-xs text-purple-700 font-bold uppercase">Marge Brute</p>
                 <p className="text-xl font-bold text-purple-900">{formatCurrency(overview ? Number(overview.marge_brute_totale) : 0)}</p>
                 <p className="text-[10px] text-purple-600 mt-0.5 flex items-center gap-1"><Search className="w-3 h-3" /> Voir détails</p>
               </div>
            </div>
         </Card>
         )}
      </div>
      )}

      {/* Charts Row 1 - CA vs Charges hidden for COMMERCIAL */}
      {canSeeFinancialDetails && (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card title="Évolution CA vs Charges" className="lg:col-span-2">
           <div className="h-80 w-full">
             {isLoading ? (
               <div className="h-full flex items-center justify-center">
                 <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
               </div>
             ) : (
             <ResponsiveContainer width="100%" height="100%">
               <BarChart
                 data={revenueData}
                 margin={{top: 10, right: 30, left: 0, bottom: 0}}
                 onClick={(state: any) => {
                   if (state?.activePayload?.[0]?.payload?.date_jour) {
                     openDrillDown({ kind: 'day_detail', date_jour: state.activePayload[0].payload.date_jour });
                   }
                 }}
                 style={{ cursor: 'pointer' }}
               >
                 <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9"/>
                 <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fill: '#64748b', fontSize: 12}} dy={10} />
                 <YAxis axisLine={false} tickLine={false} tick={{fill: '#64748b', fontSize: 12}} tickFormatter={(val) => `${val/1000000}M`} />
                 <Tooltip
                   cursor={{fill: '#f8fafc'}}
                   formatter={(val: number) => formatCurrency(val)}
                   contentStyle={{borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'}}
                 />
                 <Legend />
                 <Bar dataKey="ca" name="Chiffre d'Affaires" fill="#16a34a" radius={[4, 4, 0, 0]} barSize={20} />
                 <Bar dataKey="charges" name="Charges" fill="#94a3b8" radius={[4, 4, 0, 0]} barSize={20} />
               </BarChart>
             </ResponsiveContainer>
             )}
           </div>
        </Card>

        <Card title="Répartition CA par Provenance">
           <div className="h-80 w-full">
             {isLoading ? (
               <div className="h-full flex items-center justify-center">
                 <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
               </div>
             ) : (
             <ResponsiveContainer width="100%" height="100%">
               <PieChart>
                 <Pie
                   data={provenanceData}
                   innerRadius={60}
                   outerRadius={100}
                   paddingAngle={5}
                   dataKey="value"
                   onClick={(_: any, index: number) => {
                     const entry = provenanceData[index];
                     if (entry) openDrillDown({ kind: 'provenance_detail', provenance_nom: entry.name });
                   }}
                   style={{ cursor: 'pointer' }}
                 >
                   {provenanceData.map((entry, index) => (
                     <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                   ))}
                 </Pie>
                 <Tooltip formatter={(val: number) => formatCurrency(val)} />
                 <Legend verticalAlign="bottom" height={36}/>
               </PieChart>
             </ResponsiveContainer>
             )}
           </div>
        </Card>
      </div>

      )}

      {/* Row 2: Stock & Unpaid */}
      <div className={`grid grid-cols-1 ${canSeeFinancialDetails ? 'lg:grid-cols-2' : ''} gap-6`}>
         {/* Stock Breakdown - hidden for COMMERCIAL */}
         {canSeeFinancialDetails && (
         <Card title="Valorisation du Stock par Catégorie">
            {isLoading ? (
              <div className="h-64 flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            ) : (
            <div className="flex flex-col md:flex-row items-center gap-6">
               <div className="h-64 w-full md:w-1/2">
                 <ResponsiveContainer width="100%" height="100%">
                   <PieChart>
                     <Pie
                       data={stockChartData}
                       innerRadius={0}
                       outerRadius={80}
                       dataKey="value"
                       onClick={(_: any, index: number) => {
                         const entry = stockChartData[index];
                         if (entry) openDrillDown({ kind: 'stock_category', category_nom: entry.name });
                       }}
                       style={{ cursor: 'pointer' }}
                     >
                       {stockChartData.map((entry, index) => (
                         <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                       ))}
                     </Pie>
                     <Tooltip formatter={(val: number) => formatCurrency(val)} />
                   </PieChart>
                 </ResponsiveContainer>
               </div>
               <div className="w-full md:w-1/2 space-y-3">
                  {stockChartData.map((d, i) => (
                     <div
                       key={i}
                       className="flex justify-between items-center text-sm border-b border-slate-100 pb-2 cursor-pointer hover:bg-slate-50 rounded px-1 -mx-1"
                       onClick={() => openDrillDown({ kind: 'stock_category', category_nom: d.name })}
                     >
                        <div className="flex items-center gap-2">
                           <div className="w-3 h-3 rounded-full" style={{backgroundColor: COLORS[i % COLORS.length]}}></div>
                           <span className="text-slate-600">{d.name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-slate-800">{formatCurrency(d.value)}</span>
                          <ChevronRight className="w-3 h-3 text-slate-400" />
                        </div>
                     </div>
                  ))}
                  <div className="pt-2 mt-2 border-t border-slate-200 flex justify-between font-bold text-slate-900">
                     <span>Total</span>
                     <span>{formatCurrency(stockValuation)}</span>
                  </div>
               </div>
            </div>
            )}
         </Card>
         )}

         {/* Unpaid List */}
         <Card title="Détail des Impayés Clients">
            <div className="overflow-x-auto">
               <table className="min-w-full divide-y divide-slate-200">
                 <thead className="bg-slate-50">
                    <tr>
                       <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Client</th>
                       <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Facture</th>
                       <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Reste Dû</th>
                       <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Retard</th>
                    </tr>
                 </thead>
                 <tbody className="divide-y divide-slate-200">
                    {overdueInvoices.length > 0 ? overdueInvoices.map(inv => {
                       const resteDu = inv.montant_restant ?? inv.solde_restant ?? inv.remaining_amount ?? inv.remainingAmount ?? 0;
                       const dateEch = inv.date_echeance;
                       const joursRetard = dateEch ? Math.max(0, Math.floor((Date.now() - new Date(dateEch).getTime()) / 86400000)) : 0;
                       return (
                         <tr
                           key={inv.id}
                           className="cursor-pointer hover:bg-slate-50"
                           onClick={() => inv.id && openDrillDown({ kind: 'invoice_detail', invoice_id: inv.id })}
                         >
                            <td className="px-4 py-3 text-sm font-medium text-slate-900">{inv.client_nom || inv.clientName || '—'}</td>
                            <td className="px-4 py-3 text-sm text-blue-600 font-medium">{inv.code || inv.id}</td>
                            <td className="px-4 py-3 text-sm text-right font-bold text-red-600">{formatCurrency(resteDu)}</td>
                            <td className="px-4 py-3 text-center text-xs">
                               {joursRetard > 0 ? <Badge color="red">{joursRetard} jours</Badge> : <Badge color="yellow">Impayé</Badge>}
                            </td>
                         </tr>
                       );
                    }) : (
                       <tr><td colSpan={4} className="text-center py-4 text-slate-500">Aucun impayé.</td></tr>
                    )}
                 </tbody>
               </table>
            </div>
            <div className="mt-4 text-right">
               <Button size="sm" variant="ghost" className="text-primary-600" onClick={() => {
                 if (navigate) navigate('sales');
               }}>Voir toutes les factures</Button>
            </div>
         </Card>
      </div>

      {/* ================================================================= */}
      {/* Drill-down Drawer                                                 */}
      {/* ================================================================= */}
      {drillDown && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-slate-900/50 z-30 transition-opacity"
            onClick={closeDrillDown}
          />
          {/* Panel */}
          <div className="fixed inset-y-0 right-0 w-full md:w-[800px] bg-white shadow-2xl z-40 flex flex-col animate-in slide-in-from-right">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50 flex-shrink-0">
              <h3 className="text-lg font-bold text-slate-800">{drillDownTitle}</h3>
              <button
                onClick={closeDrillDown}
                className="p-2 hover:bg-slate-200 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-slate-600" />
              </button>
            </div>
            {/* Scrollable body */}
            <div className="flex-1 overflow-y-auto p-6">
              {renderDrillDownContent()}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
